import { useState, useEffect, useRef } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

/**
 * Fetch the "current" reportExercise for a client: first incomplete, else most recent.
 * Path: organizations/{organizationId}/clients/{clientId}/reportExercise
 * Query: orderBy createdOn desc, limit 30 (no date/status filter).
 *
 * @param {{ clientId: string | null, organizationId: string | null }} options
 * @returns {{ currentWorkout: object | null, loading: boolean, error: Error | null }}
 */
export function useCurrentWorkout({ clientId, organizationId }) {
  const [currentWorkout, setCurrentWorkout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);

  const isValid = Boolean(clientId && organizationId);

  useEffect(() => {
    if (!isValid) {
      setCurrentWorkout(null);
      setLoading(false);
      setError(null);
      return;
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
    setLoading(true);
    setError(null);

    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId)
      .collection("reportExercise")
      .orderBy("createdOn", "desc")
      .limit(30);

    unsubscribeRef.current = ref.onSnapshot(
      (snapshot) => {
        const docs = snapshot.docs;
        if (docs.length === 0) {
          setCurrentWorkout(null);
          setLoading(false);
          return;
        }
        const incomplete = docs.find((doc) => doc.data().status !== "complete");
        const chosen = incomplete || docs[0];
        const d = chosen.data();
        setCurrentWorkout({
          id: chosen.id,
          ...d,
          createdOn: d.createdOn?.toDate?.() ?? d.createdOn,
          completedOn: d.completedOn?.toDate?.() ?? d.completedOn ?? null,
        });
        setLoading(false);
      },
      (err) => {
        setError(err);
        setCurrentWorkout(null);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [clientId, organizationId, isValid]);

  return { currentWorkout, loading, error };
}
